import * as React from 'react'
import { cva, type VariantProps } from 'class-variance-authority'
import { Loader2 } from 'lucide-react'
import { cn } from '@/lib/utils'

const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md font-medium transition-all duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:pointer-events-none disabled:opacity-50',
  {
    variants: {
      // Variant = Visual importance (how prominent should this action be?)
      variant: {
        // Primary: THE main action - filled, high contrast
        default:
          'bg-primary text-primary-foreground shadow-sm hover:bg-primary/90 active:scale-[0.98]',
        // Secondary: Alternative action - muted fill
        secondary:
          'bg-secondary text-secondary-foreground hover:bg-secondary/80 active:scale-[0.98]',
        // Outline: Tertiary action - bordered, transparent
        outline:
          'border border-input bg-transparent hover:bg-accent hover:text-accent-foreground active:scale-[0.98]',
        // Ghost: Minimal action - no border, no fill (toolbars, dense UIs)
        ghost:
          'hover:bg-accent hover:text-accent-foreground active:bg-accent/80',
        // Destructive: Dangerous action - red, demands attention
        destructive:
          'bg-destructive text-destructive-foreground shadow-sm hover:bg-destructive/90 active:scale-[0.98]',
        // Link: Inline text action
        link: 'text-primary underline-offset-4 hover:underline p-0 h-auto',
      },
      // Size = Physical dimensions (where is this button placed?)
      size: {
        // xs: Dense UIs (tables, inline actions) - 32px
        xs: 'h-8 px-3 text-xs rounded',
        // sm: Compact areas (card actions, secondary buttons) - 36px
        sm: 'h-9 px-4 text-sm',
        // default: Standard forms and dialogs - 40px
        default: 'h-10 px-5 text-sm',
        // lg: Prominent CTAs within pages - 48px
        lg: 'h-12 px-6 text-base',
        // xl: Hero sections, landing page CTAs - 56px
        xl: 'h-14 px-10 text-lg rounded-lg',
        // Icon buttons - square with consistent touch targets
        icon: 'h-10 w-10 p-0',
        'icon-sm': 'h-9 w-9 p-0',
        'icon-xs': 'h-8 w-8 p-0',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  loading?: boolean
  icon?: React.ReactNode
  iconPosition?: 'left' | 'right'
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, loading, icon, iconPosition = 'left', children, disabled, ...props }, ref) => {
    const isDisabled = disabled || loading

    return (
      <button
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        disabled={isDisabled}
        aria-busy={loading || undefined}
        aria-disabled={isDisabled || undefined}
        {...props}
      >
        {loading ? (
          <>
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
            {children}
          </>
        ) : (
          <>
            {icon && iconPosition === 'left' && (
              <span className="shrink-0" aria-hidden="true">{icon}</span>
            )}
            {children}
            {icon && iconPosition === 'right' && (
              <span className="shrink-0" aria-hidden="true">{icon}</span>
            )}
          </>
        )}
      </button>
    )
  }
)
Button.displayName = 'Button'

export { Button, buttonVariants }
